import { useCallback, useEffect, useState } from "react";
import { Button, Card, Form, Input, message, Modal, Popconfirm, Spin, Typography } from "antd";

import { useNavigate } from "react-router";
import { ArrowLeftOutlined,DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  SECURITY_QUESTION_ADD_API_URL,
  SECURITY_QUESTION_DELETE_API_BASE_URL,
  SECURITY_QUESTIONS_API_URL,
} from "../../config/constants";
import "../../styles/trackon.css";
import { apiFetch } from "../../utils/apiClient";
import DataState from "../../components/DataState/DataState";

const { Title, Text } = Typography;

const tableWrapStyle = {
  overflowX: "auto",
  minHeight: 80,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  background: "#ffffff",
};

const thStyle = {
  border: "1px solid #e6e6e6",
  padding: "8px 10px",
  background: "#f5f5f5",
  fontWeight: 600,
  fontSize: 12,
  color: "#595959",
  textAlign: "left",
};

const tdStyle = {
  border: "1px solid #e6e6e6",
  padding: "6px 10px",
  verticalAlign: "middle",
  fontSize: 13,
};





const extractApiMessage = (payload) => {
  if (typeof payload === "string") {
    const trimmed = payload.trim();
    return trimmed;
  }

  if (payload && typeof payload === "object") {
    const obj = payload;
    const direct = typeof obj.message === "string" ? obj.message.trim() : "";
    if (direct) return direct;

    const nestedData = obj.data && typeof obj.data === "object"
      ? (obj.data)
      : null;

    const nested = nestedData && typeof nestedData.message === "string"
      ? nestedData.message.trim()
      : "";
    if (nested) return nested;
  }

  return "";
};

const getAuthHeader = () => {
  const rawToken =
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("accessToken");
  const tokenType = sessionStorage.getItem("tokenType") || "Bearer";

  if (!rawToken) {
    throw new Error("Authentication token not found. Please log in again.");
  }

  const normalizedToken = rawToken.replace(/^"|"$/g, "").replace(/^Bearer\s+/i, "").trim();
  return `${tokenType} ${normalizedToken}`;
};

const toPositiveNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
};

const parseRows = (payload) => {
  let source = [];
  let total = 0;

  if (Array.isArray(payload)) {
    source = payload;
    total = payload.length;
  } else if (payload && typeof payload === "object") {
    const obj = payload ;
    const nestedData = obj.data && typeof obj.data === "object" ? (obj.data) : null;

    if (Array.isArray(obj.content)) {
      source = obj.content;
    } else if (Array.isArray(obj.items)) {
      source = obj.items;
    } else if (Array.isArray(obj.data)) {
      source = obj.data;
    } else if (nestedData && Array.isArray(nestedData.content)) {
      source = nestedData.content;
    } else if (nestedData && Array.isArray(nestedData.items)) {
      source = nestedData.items;
    }

    total =
      toPositiveNumber(obj.totalElements) ??
      toPositiveNumber(obj.totalCount) ??
      toPositiveNumber(obj.total) ??
      toPositiveNumber(obj.count) ??
      toPositiveNumber(nestedData?.totalElements) ??
      toPositiveNumber(nestedData?.totalCount) ??
      toPositiveNumber(nestedData?.total) ??
      toPositiveNumber(nestedData?.count) ??
      source.length;
  }

  const rows = source
    .map((item) => item)
    .map((item) => {
      const id = item.quesId ?? item.id;
      const question = item.question ?? item.securityQuestion ?? item.label;
      if (id === undefined || id === null || !question) {
        return null;
      }
      return {
        id: String(id),
        question: String(question),
      };
    })
    .filter((item) => item !== null);

  return {
    rows,
    total,
  };
};

export function SecurityQuestionsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  // Starts true so the first render shows the skeleton, not an empty table.
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [form] = Form.useForm();

  // Derived in the same render as the request state, so no old rows show while a request runs.
  const status = loading ? "loading" : loadError ? "error" : "ready";

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const authHeader = getAuthHeader();
      const res = await apiFetch(SECURITY_QUESTIONS_API_URL, {
        method: "GET",
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      }, "Unable to load security questions.");

   
      const data = await res.json();
      const parsed = parseRows(data);
      setRows(parsed.rows);
      setTotalQuestions(parsed.total);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unable to load security questions.";
      // Shown by <ErrorState /> only; no toast, so StrictMode's double effect run cannot duplicate it.
      setLoadError(errorMessage);
      setRows([]);
      setTotalQuestions(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();
      setAddLoading(true);
      const authHeader = getAuthHeader();

      const res = await apiFetch(SECURITY_QUESTION_ADD_API_URL, {
        method: "POST",
        headers: {
          Authorization: authHeader,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ question: values.question.trim() }),
      }, "Failed to add security question.", { passthrough: [400] });

      if (!res.ok) {
        let errorMessage = `Failed to add security question (${res.status})`;
        try {
          const data = (await res.json());
          if (data?.message) {
            errorMessage = data.message;
          }
        } catch {
          // keep fallback message
        }
        throw new Error(errorMessage);
      }

      const data = (await res.json().catch(() => ({})));
      if (typeof data?.message === "string" && data.message.trim()) {
        message.success(data.message);
      }
      setAddModalOpen(false);
      form.resetFields();
      await fetchRows();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unable to add security question.";
      message.error(errorMessage);
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (record) => {
    setDeletingId(record.id);
    let shouldRefresh = false;
    try {
      const authHeader = getAuthHeader();
      const res = await apiFetch(
        `${SECURITY_QUESTION_DELETE_API_BASE_URL}/${encodeURIComponent(record.id)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: authHeader,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
        "Failed to delete security question.",
        { passthrough: [400] },
      );
      shouldRefresh = true;

      const rawBody = await res.text();
      let parsedBody = rawBody;
      if (rawBody) {
        try {
          parsedBody = JSON.parse(rawBody);
        } catch {
          parsedBody = rawBody;
        }
      }

      const apiMessage = extractApiMessage(parsedBody);

      if (!res.ok) {
        const errorMessage = apiMessage || `Failed to delete security question (${res.status})`;
        throw new Error(errorMessage);
      }

      if (apiMessage) {
        const lowered = apiMessage.toLowerCase();
        if (lowered.includes("cannot") || lowered.includes("already being used") || lowered.includes("deactivated")) {
          message.error(apiMessage);
          return;
        }
        message.success(apiMessage);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unable to delete security question.";
      message.error(errorMessage);
    } finally {
      if (shouldRefresh) {
        await fetchRows();
      }
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div
  style={{
    marginBottom: 16,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }}
>
  <div>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/watchtower/settings")}
      />
      <Title level={4} style={{ margin: 0 }}>
        Security Questions
      </Title>
    </div>

  </div>
        {status === "ready" && (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
          Add Question
        </Button>
        )}
      </div>

      <DataState
        status={status}
        errorTitle="Couldn't load security questions"
        error={loadError}
        onRetry={() => {
          void fetchRows();
        }}
        skeletonRows={6}
      >
      <Card bordered={false}>
        <div style={{ ...tableWrapStyle, maxWidth: 720 }}>
          <>
          <Spin spinning={loading}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Question</th>
                  <th style={{ ...thStyle, width: 90, textAlign: "center" }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td style={tdStyle} colSpan={2}>
                      <Text style={{ color: "#8c8c8c", fontSize: 13 }}>
                        No security questions found.
                      </Text>
                    </td>
                  </tr>
                ) : (
                  rows.map((record) => (
                    <tr key={record.id}>
                      <td style={tdStyle}>{record.question}</td>
                      <td style={{ ...tdStyle, width: 90, textAlign: "center" }}>
                        <Popconfirm
                          overlayClassName="trackon-root"
                          title="Delete security question"
                          description={
                            <div>
                              <div>
                                Security question cannot be deleted while assigned to users!
                              </div>
                              <div style={{ marginTop: 8 }}>Are you sure you want to continue?</div>
                            </div>
                          }
                          okText="Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true, loading: deletingId === record.id }}
                          onConfirm={() => {
                            void handleDelete(record);
                          }}
                        >
                          <DeleteOutlined
                            style={{
                              fontSize: 16,
                              cursor: "pointer",
                              color: "#ff4d4f",
                              opacity: deletingId === record.id ? 0.5 : 1,
                            }}
                          />
                        </Popconfirm>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Spin>
          </>
        </div>
      </Card>
      </DataState>

      <Modal
        title="Add Security Question"
        open={addModalOpen}
        onOk={() => {
          void handleAdd();
        }}
        okText="Add"
        cancelText="Cancel"
        confirmLoading={addLoading}
        onCancel={() => {
          setAddModalOpen(false);
          form.resetFields();
        }}
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            label="Question"
            name="question"
            rules={[
              { required: true, message: "Please enter a security question." },
              { whitespace: true, message: "Question cannot be empty." },
            ]}
          >
            <Input.TextArea rows={3} maxLength={250} placeholder="Enter security question" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
